import { Flare } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";

import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import {
  actions,
  PlotPanelState,
  useVizDispatch,
} from "@/shared/state/visualization";
import { DEFAULT_PLOT_PANEL_CONFIG } from "@/shared/state/visualization/context";

import { usePlotPanelContext } from "../panelContext";

const DISABLED_EXPLANATION =
  "Event markers are unavailable while the x axis is aligned";

interface DisplayEventsButtonProps {
  state: PlotPanelState;
}

export function DisplayEventsButton({ state }: DisplayEventsButtonProps) {
  const dispatch = useVizDispatch();
  const { isAligned } = usePlotPanelContext();
  const showEvents =
    state.config?.showEvents ?? DEFAULT_PLOT_PANEL_CONFIG.showEvents;

  // Event markers sit at absolute times, and an aligned panel plots each series
  // in elapsed time from its own anchor, so the plot draws no markers there. The
  // icon drops its active tint too, which would otherwise claim markers are on
  // screen.
  const button = (
    <IconButton
      aria-label="display-events-toggle"
      disabled={isAligned}
      onClick={() => {
        dispatch(
          actions.setPlotPanelConfig(state.id, {
            showEvents: !showEvents,
          }),
        );
      }}
      size="small"
      title={isAligned ? undefined : "Show/hide events"}
    >
      <Flare
        color={showEvents && !isAligned ? "primary" : "inherit"}
        fontSize="small"
      />
    </IconButton>
  );

  if (!isAligned) {
    return button;
  }

  // Browsers suppress the native title tooltip on a disabled control, so the
  // explanation would be missing exactly when the user needs it. A disabled
  // button also emits no pointer events for the tooltip to listen for, so the
  // span carries them instead.
  return (
    <RobotoTooltip title={DISABLED_EXPLANATION}>
      <span>{button}</span>
    </RobotoTooltip>
  );
}
